import React, { useState } from "react";
import { Card, Row, Col, Badge, Button, Collapse } from "react-bootstrap";

// Permission Change Row Component
const PermissionChangeRow = ({ permission, type }) => {
  return (
    <Card
      className="border mb-2 background-secondary"
      style={{
        borderLeft: `4px solid ${type === "added" ? "#28a745" : "#dc3545"}`,
      }}
    >
      <Card.Body className="py-2">
        <div className="d-flex align-items-center">
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center mr-3 ${
              type === "added" ? "bg-success" : "bg-danger"
            }`}
            style={{ width: "24px", height: "24px", minWidth: "24px" }}
          >
            <i
              className={`fa ${
                type === "added" ? "fa-plus" : "fa-minus"
              } text-white`}
              style={{ fontSize: "10px" }}
            ></i>
          </div>
          <div className="flex-grow-1">
            <div
              className="font-weight-bold color-dark"
              style={{ fontSize: "0.875rem" }}
            >
              {permission.name}
            </div>
            <small
              className="text-muted font-weight-bold"
              style={{ fontSize: "0.75rem", fontFamily: "monospace" }}
            >
              {permission.id}
            </small>
          </div>
          <Badge
            variant={type === "added" ? "success" : "danger"}
            className="border"
            style={{ fontWeight: 600, fontSize: "0.75rem" }}
          >
            {type === "added" ? "Added" : "Removed"}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  );
};

// Role Changes Section
const RoleChangesSection = ({ role }) => {
  const [expanded, setExpanded] = useState(false);

  const addedPermissions = role.permissions.filter((p) => p.isAssigned);
  const removedPermissions = role.permissions.filter((p) => !p.isAssigned);

  const hasChanges =
    addedPermissions.length > 0 || removedPermissions.length > 0;

  return (
    <Card className="border mb-3 card-primary">
      {/* Role Header */}
      <Card.Header
        className="background-primary py-3"
        style={{
          cursor: "pointer",
          borderBottom: expanded ? "1px" : "none",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div
              className="bg-primary rounded-circle d-flex align-items-center justify-content-center mr-3"
              style={{ width: "32px", height: "32px", minWidth: "32px" }}
            >
              <i
                className="fa fa-shield text-white"
                style={{ fontSize: "14px" }}
              ></i>
            </div>
            <div>
              <h6 className="font-weight-bold mb-0 color-dark">{role.name}</h6>
              <div className="d-flex align-items-center gap-2 mt-1">
                {addedPermissions.length > 0 && (
                  <Badge
                    variant="success"
                    className="border d-flex align-items-center"
                    style={{ fontSize: "0.7rem" }}
                  >
                    <i
                      className="fa fa-plus mr-1"
                      style={{ fontSize: "8px" }}
                    ></i>
                    {addedPermissions.length} added
                  </Badge>
                )}
                {removedPermissions.length > 0 && (
                  <Badge
                    variant="danger"
                    className="border d-flex align-items-center"
                    style={{ fontSize: "0.7rem" }}
                  >
                    <i
                      className="fa fa-minus mr-1"
                      style={{ fontSize: "8px" }}
                    ></i>
                    {removedPermissions.length} removed
                  </Badge>
                )}
                {!hasChanges && (
                  <Badge
                    variant="secondary"
                    className="border d-flex align-items-center"
                    style={{ fontSize: "0.7rem" }}
                  >
                    <i
                      className="fa fa-minus mr-1"
                      style={{ fontSize: "8px" }}
                    ></i>
                    No changes
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="link"
            className="p-0 color-dark"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            <i
              className={`fa ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
            ></i>
          </Button>
        </div>
      </Card.Header>

      {/* Permission Changes */}
      <Collapse in={expanded}>
        <Card.Body className="py-3 background-primary">
          <div className="d-flex flex-column gap-2">
            {/* Added Permissions */}
            {addedPermissions.length > 0 && (
              <div>
                <small
                  className="font-weight-bold text-success text-uppercase d-block mb-2"
                  style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
                >
                  Added Permissions
                </small>
                <div className="d-flex flex-column gap-1">
                  {addedPermissions.map((permission) => (
                    <PermissionChangeRow
                      key={permission.id}
                      permission={permission}
                      type="added"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Removed Permissions */}
            {removedPermissions.length > 0 && (
              <div>
                <small
                  className="font-weight-bold text-danger text-uppercase d-block mb-2"
                  style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
                >
                  Removed Permissions
                </small>
                <div className="d-flex flex-column gap-1">
                  {removedPermissions.map((permission) => (
                    <PermissionChangeRow
                      key={permission.id}
                      permission={permission}
                      type="removed"
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No Changes Message */}
            {!hasChanges && (
              <div className="text-center py-3">
                <i className="fa fa-info-circle text-muted fa-2x mb-2"></i>
                <div className="text-muted font-weight-bold">
                  No permission changes for this role
                </div>
              </div>
            )}
          </div>
        </Card.Body>
      </Collapse>
    </Card>
  );
};

// Changes Overview
const ChangesOverview = ({ roles }) => {
  const totalAdded = roles.reduce(
    (acc, role) => acc + role.permissions.filter((p) => p.isAssigned).length,
    0
  );
  const totalRemoved = roles.reduce(
    (acc, role) => acc + role.permissions.filter((p) => !p.isAssigned).length,
    0
  );
  const totalChanges = totalAdded + totalRemoved;

  const rolesWithChanges = roles.filter(
    (role) =>
      role.permissions.filter((p) => p.isAssigned).length > 0 ||
      role.permissions.filter((p) => !p.isAssigned).length > 0
  ).length;

  return (
    <Card className="border mb-4 card-primary">
      <Card.Body className="p-4">
        <h6 className="font-weight-bold color-dark mb-3">
          Permission Changes Overview
        </h6>
        <hr className="my-3" />
        <Row>
          <Col xs={6} sm={3} className="text-center mb-3">
            <div
              className="font-weight-bold primary-color"
              style={{ fontSize: "2rem" }}
            >
              {rolesWithChanges}
            </div>
            <small className="text-muted font-weight-bold d-block">
              Roles Modified
            </small>
          </Col>
          <Col xs={6} sm={3} className="text-center mb-3">
            <div
              className="font-weight-bold text-success"
              style={{ fontSize: "2rem" }}
            >
              +{totalAdded}
            </div>
            <small className="text-muted font-weight-bold d-block">
              Permissions Added
            </small>
          </Col>
          <Col xs={6} sm={3} className="text-center mb-3">
            <div
              className="font-weight-bold text-danger"
              style={{ fontSize: "2rem" }}
            >
              -{totalRemoved}
            </div>
            <small className="text-muted font-weight-bold d-block">
              Permissions Removed
            </small>
          </Col>
          <Col xs={6} sm={3} className="text-center mb-3">
            <div
              className="font-weight-bold color-dark"
              style={{ fontSize: "2rem" }}
            >
              {totalChanges}
            </div>
            <small className="text-muted font-weight-bold d-block">
              Total Changes
            </small>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// Main Permission Changes Component
const PermissionChanges = ({ workQueueData }) => {
  const roles = workQueueData?.roleAndPermissions?.roles || [];

  return (
    <div className="w-100">
      {/* Changes Overview */}
      <ChangesOverview roles={roles} />

      {/* Role Changes */}
      <Card className="border card-primary">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="font-weight-bold color-dark mb-0">
              Role Permission Changes
            </h6>
            <Badge variant="primary" className="font-weight-bold">
              {roles.length} Roles
            </Badge>
          </div>
          <div className="d-flex flex-column gap-3">
            {roles.map((role) => (
              <RoleChangesSection key={role.id} role={role} />
            ))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PermissionChanges;
